import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

import { Subscription, from, map } from 'rxjs';
import { merge, uniq, flatten, get } from 'lodash';
import { AnalyticsApiService, BenchmarkService, ColorService, DEFAULT_EXCLUDE_FALSY_TOOGLE, DateRangeValue, DatesService, Dimension, DynamicDateRange, EMPTY_FALSY_ERROR, MessageService, Metric, Query, Result, TrackingService, WidgetQueueProxyService } from '@hub-components';

import { DEFAULT_COMPARE_TO } from 'app/apps/studio/app/constants';
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
import { DEFAULT_CHART_STYLE_OPTIONS, DEFAULT_OPTIONS } from '../default-chart-options';

const SUPPORTED_INPUTS = [
  {
    bindTo: 'dimensions',
    label: 'Dimension',
    type: 'dimension',
  }, {
    bindTo: 'metrics',
    label: 'Metrics',
    type: 'metric',
  },
];

const HIGHCHARTS_OPTIONS = {
  chart: {
    type: null,
  },
  plotOptions: {
    series: {
      threshold: 0,
      softThreshold: true,
    },
  },
};

@Component({
  selector: 'combo-chart',
  template: '<app-chart *ngIf="series" [series]="series" [comparisonSeries]="comparisonSeries" [comparisonPeriod]="comparisonPeriod" [loadingBenchmarks]="loadingBenchmarks" [footer]="footer" [footerData]="footerData" [datasetDisplayName]="datasetDisplayName" [highchartsOptions]="highchartsOptions" [chartOptions]="chartOptions" [style]="style" [widgetId]="widgetId" [queryFirstDayOfWeek]="result?.query?.firstDayOfWeek"></app-chart>',
  styles: [
    `
    :host {
      height: 100%;
    }
  `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboChartComponent implements OnChanges, OnDestroy {
  @HostBinding('style.backgroundColor') backgroundColor: string;

  @Input() result: Result;
  @Input() dimensions: Dimension[];
  @Input() metrics: Metric[];
  @Input() style: WidgetStyle;
  @Input() chartOptions: ChartOptions = {};
  @Input() dashboardInterval: DynamicDateRange;

  static supportedInputs = SUPPORTED_INPUTS;
  static supportedOptions = [
    {
      bindTo: 'rollup',
      label: 'Sum by',
      type: 'rollupDropdown',
    },
    {
      bindTo: 'limit',
      label: 'Number of results',
      type: 'limitDropdown',
    },
    {
      bindTo: 'dataOptions',
      label: 'Data',
      type: 'dataOptions',
      options: [
        {
          bindTo: 'excludeFalsy',
        },
      ],
    },
    ...DEFAULT_OPTIONS,
    {
      bindTo: 'comboMetric',
      label: 'Display as line',
      type: 'comboMetricDropdown',
    },
    {
      bindTo: 'chartTooltip',
      label: 'Tooltip',
      type: 'chartTooltip',
      options: [
        {
          bindTo: 'singleValueOnHover',
        },
      ],
    },
    {
      bindTo: 'dataLabels',
      label: 'Data labels',
      type: 'textFormat',
      options: [
        {
          bindTo: 'rotation',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'yAxis',
      label: 'Y-Axis',
      type: 'yAxis',
      options: [
        {
          bindTo: 'min_left',
        }, {
          bindTo: 'max_left',
        }, {
          bindTo: 'size_left',
        }, {
          bindTo: 'min_right',
        }, {
          bindTo: 'max_right',
        }, {
          bindTo: 'size_right',
        }, {
          bindTo: 'axis',
        }, {
          bindTo: 'showLabels',
        }, {
          bindTo: 'sharedAxis',
        }, {
          bindTo: 'normalizeYAxis',
        },
      ],
    },
    {
      bindTo: 'xAxis',
      label: 'X-Axis',
      type: 'xAxis',
      options: [
        {
          bindTo: 'size',
        },
      ],
    },
    {
      bindTo: 'dateRange',
      label: 'Date range',
      type: 'dateRange',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'value',
        },
      ],
    },
    {
      bindTo: 'compareTo',
      label: 'Compare To',
      type: 'compareToDropdown',
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = DEFAULT_CHART_STYLE_OPTIONS;
  static supportedRollups = ['day', 'week', 'month', 'quarter', 'year'];
  static supportedComparisons = ['none', 'previous_period', 'previous_year'];
  highchartsOptions: any;
  public series: any[];
  public comparisonSeries: any[];
  public comparisonPeriod: string;
  public loadingBenchmarks: boolean = false;
  private _series: any[] = [];
  private _filteredSeries: any[] = [];
  private _comparisonSeries: any[] = [];
  private _filteredComparisonSeries: any[] = [];
  private excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;
  private isFilterEmpty = false;
  private compareTo = DEFAULT_COMPARE_TO;
  private benchmarkResultsSubscription: Subscription;
  private singleValueOnHover: boolean;

  datasetDisplayName: string;
  footer: any;
  footerData: any;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private colorService: ColorService,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private datesService: DatesService,
    private benchmarkService: BenchmarkService,
    private widgetQueueProxy: WidgetQueueProxyService,
  ) {
    this.highchartsOptions = merge({}, HIGHCHARTS_OPTIONS);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.result) {
      this.datasetDisplayName = <string> this.result.query.schema.displayName;
      this._series = this.getSeriesData(this.result, false);
      this._filteredSeries = this.getSeriesData(this.result, true);
      this.isFilterEmpty = this._filteredSeries.length === 0 && this._series.length !== 0;
      if (this.isFilterEmpty && this.excludeFalsy) {
        this.messageService.info(EMPTY_FALSY_ERROR);
      }
      this.updateSeriesData();

      if (!this.chartOptions) {
        this.chartOptions = {};
      }

      if (this.chartOptions.dataOptions) {
        if (this.chartOptions.dataOptions.excludeFalsy !== undefined) {
          if (!this.excludeFalsy && this.isFilterEmpty && this.chartOptions.dataOptions.excludeFalsy) {
            this.messageService.info(EMPTY_FALSY_ERROR);
          }
          if (this.excludeFalsy !== this.chartOptions.dataOptions.excludeFalsy) {
            this.addTracking('excludeFalsy', changes);
            this.excludeFalsy = this.chartOptions.dataOptions.excludeFalsy;
          }
          this.updateSeriesData();
        }
      }

      if (this.chartOptions.footer) {
        if (this.footer !== undefined) {
          if (this.footer.dateRange !== this.chartOptions.footer?.dateRange) {
            this.addTracking('footerDateRange', changes);
          }
          if (this.footer.datasetInfo !== this.chartOptions.footer?.datasetInfo) {
            this.addTracking('footerDatasetInfo', changes);
          }
        }

        this.footer = this.chartOptions.footer;
        this.footerData = {
          datasetInfo: this.result.query.schema.displayName,
          dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
        };
      }

      this.chartOptions.hideXAxis = this.result.query.rollup.value === 'total';
      this.chartOptions.dateType = this.result.query.rollup.value;

      if (this.style) {
        this.setStyle();
      }

      if (this.chartOptions?.compareTo) {
        if (this.compareTo.value !== this.chartOptions.compareTo.value) {
          this.addTracking('compareTo', changes);
        }
        this.compareTo = this.chartOptions.compareTo;
        this.comparisonPeriod = this.compareTo.name.toLowerCase();
        if (this.compareTo.value !== 'none' && this.result.query) {
          const benchmarkDates =
            (this.chartOptions.dateRange && this.chartOptions.dateRange.enabled) ?
              this.getBenchmarkDates(this.chartOptions.dateRange.value) :
              this.getBenchmarkDates(this.dashboardInterval);
          if (benchmarkDates) {
            this.loadingBenchmarks = true;
            const benchmarkBase = this.result.query.clone();
            benchmarkBase.interval = this.intervalFromDates(benchmarkDates);
            benchmarkBase.auth = this.chartOptions.compareTo.query && this.chartOptions.compareTo.query['auth'];
            this.chartOptions.compareTo.query = merge(benchmarkBase.asJson(), { auth: benchmarkBase.auth }, { deep: true });
            const benchmarkResults = this.runQuery(benchmarkBase);
            this.updateBenchmark(benchmarkResults);
          } else {
            this.deleteComparisonSeries();
          }
        } else {
          this.deleteComparisonSeries();
        }
      }
    }

    if (this.chartOptions?.chartTooltip && this.chartOptions.chartTooltip.singleValueOnHover !== undefined) {
      if (this.singleValueOnHover !== undefined && this.chartOptions.chartTooltip.singleValueOnHover !== this.singleValueOnHover) {
        this.addTracking('singleValueOnHover', changes);
      }
      this.singleValueOnHover = this.chartOptions.chartTooltip.singleValueOnHover;
    }
  }

  ngOnDestroy(): void {
    if (this.benchmarkResultsSubscription) {
      this.benchmarkResultsSubscription.unsubscribe();
    }
  }

  private setStyle() {
    if (this.style.enableColorPerChartValue && this.style.colorPerChartValues?.length) {
      this.chartOptions.colors = this.style.colorPerChartValues.map(colorMap => colorMap.color);
    } else if (this.style.baseColor) {
      this.chartOptions.colors = this.colorService.getSimilarColorSeries(this.style.baseColor, this.series.length);
    } else {
      this.chartOptions.colors = this.colorService.defaultColors;
    }

    this.chartOptions.textColor = this.style.textColor;
    this.chartOptions.backgroundColor = this.style.backgroundColor;
  }

  private deleteComparisonSeries(): void {
    this._comparisonSeries = [];
    this._filteredComparisonSeries = [];
    this.comparisonSeries = [];
  }

  private getBenchmarkDates(dates: DateRangeValue) {
    if (this.compareTo?.value !== 'none') {
      return this.benchmarkService.calculateBenchmark(dates, this.compareTo.value, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
    } else {
      return null;
    }
  }

  private intervalFromDates(dates) {
    return this.datesService.fixedQueryInterval(this.datesService.parseValue(dates, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined));
  }

  private runQuery(query: Query) {
    return this.widgetQueueProxy
      .analiticsQueueProxy(query, { skipLoader:true, cache:true })
      .pipe(
        map(qr => Result.parse(qr)),
      );
  }

  private updateBenchmark(benchmarkResults) {
    this.benchmarkResultsSubscription = from(benchmarkResults).subscribe((benchmarkResult: Result) => {
      this._comparisonSeries = this.getSeriesData(benchmarkResult, false, true);
      this._filteredComparisonSeries = this.getSeriesData(benchmarkResult, true, true);
      if (this.excludeFalsy) {
        this.comparisonSeries = this._filteredComparisonSeries;
      } else {
        this.comparisonSeries = this._comparisonSeries;
      }
      this.loadingBenchmarks = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  private getSeriesData(result: Result, excludeFalsy: boolean, benchmark = false) {
    const dimensionIndex = result.dimensions.length - 1;
    const dimensionValues = uniq(result.series.map(ss => ss.slices[dimensionIndex].value));
    const comboMetric = this.chartOptions?.comboMetric?.name || result.metrics[0]?.displayName;
    return flatten(result.series.map(ss => {
      const slice = ss.slices[dimensionIndex];
      return ss.metrics
        .filter(metric => excludeFalsy ? metric.values.some(value => !!value) : true)
        .map(metric => {
          const data = this.result.timestamps.map((t, i) => [t, metric.values[i]]);
          const nameParts = [];
          if (dimensionValues.length > 1 || ss.metrics.length === 1) {
            nameParts.push(slice.value);
          }
          if (ss.metrics.length > 1) {
            nameParts.push(metric.displayName);
          }
          const type = metric.displayName === comboMetric ? 'line' : 'column';
          return {
            type: type,
            name: benchmark ? `${nameParts.join(' - ')} (${this.comparisonPeriod})` : nameParts.join(' - '),
            data: data,
            format: metric.format,
            originalName: metric.name,
            displayName: metric.displayName,
            zIndex: type === 'line' ? 1000 : null,
          };
        });
    }));
  }

  private updateSeriesData() {
    this.series = this.excludeFalsy ? this._filteredSeries : this._series;
    this.comparisonSeries = this.excludeFalsy ? this._filteredComparisonSeries : this._comparisonSeries;
  }

  private addTracking(type: string, changes: SimpleChanges) {
    if (changes?.options) {
      const { currentValue, previousValue } = changes?.options;

      if (type === 'excludeFalsy' && previousValue?.dataOptions && currentValue?.dataOptions) {
        if (previousValue.dataOptions.excludeFalsy !== currentValue.dataOptions.excludeFalsy) {
          this.trackingService.track('Combo Chart Widget: Exclude N/A and 0 values', { value: this.chartOptions.dataOptions.excludeFalsy });
        }
      }

      if (previousValue?.footer && currentValue?.footer) {
        if (type === 'footerDateRange' && previousValue?.footer?.dateRange !== currentValue?.footer?.dateRange) {
          this.trackingService.track('Combo Chart Widget: Footer DataRange', { value: this.chartOptions.footer.dateRange });
        }
        if (type === 'footerDatasetInfo' && previousValue?.footer?.datasetInfo !== currentValue?.footer?.datasetInfo) {
          this.trackingService.track('Combo Chart Widget: Footer Dataset Info', { value: this.chartOptions.footer.datasetInfo });
        }
      }

      if (type === 'compareTo' && previousValue?.compareTo && currentValue?.compareTo) {
        if (previousValue?.compareTo.value !== currentValue?.compareTo.value) {
          this.trackingService.track('Combo Chart Widget: Compare To', { value: this.chartOptions.compareTo.name });
        }
      }

      if (type === 'singleValueOnHover' && previousValue?.chartTooltip?.singleValueOnHover !== currentValue?.chartTooltip?.singleValueOnHover) {
        this.trackingService.track('Combo Chart Widget: Single Value On Hover', { value: this.chartOptions.chartTooltip.singleValueOnHover });
      }
    }
  }
}
